import { RoomData } from '@/editor/data/room/Room';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { Polylabel } from '@/editor/math/Polylabel';
import { globalStores } from '@/editor/stores';
import { worldPosToClientPos } from '@/editor/utils';
import { getPathFromEdges } from '@/editor/utils/edge';
import { dispersePath } from '@/editor/utils/shape';
import { TextInfo, ITextInfoConstructor } from './textInfo';

export interface IRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

interface IRoomTextInfoConstructor extends ITextInfoConstructor<RoomData> {
  withRegion: boolean;
}

interface IRoomTextInfoUpdateParams extends ITextInfoConstructor<RoomData> {
  withRegion?: boolean;
}

/** 避让候选偏移（屏幕像素），按偏移距离从小到大排列 */
const AVOID_CANDIDATE_OFFSETS: IPoint2d[] = [
  { x: 0, y: 0 },
  { x: 0, y: -56 },
  { x: 0, y: 56 },
  { x: -80, y: 0 },
  { x: 80, y: 0 },
  { x: -64, y: -48 },
  { x: 64, y: -48 },
  { x: -64, y: 48 },
  { x: 64, y: 48 },
  { x: 0, y: -96 },
  { x: 0, y: 96 },
  { x: -128, y: 0 },
  { x: 128, y: 0 }
];

/** 候选达标阈值：与分区标签重叠不超过 5%，且基本完全在房间内 */
const GOOD_OVERLAP_RATIO = 0.05;
const GOOD_INSIDE_RATIO = 0.995;

export class RoomTextInfo extends TextInfo<RoomData> {
  private _withRegion = false;
  private _data: RoomData;

  constructor({ data, name, withRegion, visible }: IRoomTextInfoConstructor) {
    super({ data, name, visible });
    this._data = data;
    this._element.setAttribute('data-type', 'room');
    this._setWithRegion(withRegion);
  }

  private _setWithRegion(withRegion: boolean) {
    if (this._withRegion === withRegion) return;

    this._withRegion = withRegion;
    this._element.setAttribute('data-with-region', withRegion ? 'true' : 'false');
  }

  override update(params: IRoomTextInfoUpdateParams) {
    this._data = params.data;
    super.update(params);
    this._setWithRegion(!!params.withRegion);
  }

  /** 更新位置，并避让给定的分区标签矩形（屏幕坐标） */
  updatePositionAvoiding(regionRects: IRect[]) {
    this.updatePosition();

    const offset = this._resolveAvoidOffset(regionRects);
    if (this.setPositionOffset(offset)) {
      this.updatePosition();
    }
  }

  /** 计算避让分区标签的最优偏移，纯数学计算，不读写 DOM（自身尺寸测量除外） */
  private _resolveAvoidOffset(regionRects: IRect[]): IPoint2d {
    if (!regionRects.length) {
      return { x: 0, y: 0 };
    }

    const { width, height } = this.getBoundingClientRect();
    if (width <= 0 || height <= 0) {
      return { x: 0, y: 0 };
    }

    const roomPolygon = this._getScreenPolygon();
    if (roomPolygon.length < 3) {
      return { x: 0, y: 0 };
    }

    const size = { width, height };
    let bestOffset: IPoint2d = { x: 0, y: 0 };
    let bestScore = Number.POSITIVE_INFINITY;
    let bestOverlapRatio = 1;
    let bestInsideRatio = 0;

    for (const candidate of AVOID_CANDIDATE_OFFSETS) {
      const { score, overlapRatio, insideRatio } = this._scoreOffset(candidate, size, regionRects, roomPolygon);

      if (score < bestScore) {
        bestScore = score;
        bestOffset = { ...candidate };
        bestOverlapRatio = overlapRatio;
        bestInsideRatio = insideRatio;
      }

      if (insideRatio >= GOOD_INSIDE_RATIO && overlapRatio <= GOOD_OVERLAP_RATIO) {
        break;
      }
    }

    // 固定候选都避不开时，用最难抵达点兜底：
    // 把分区标签矩形当作洞，找离房间边界和分区标签都最远的点
    if (bestOverlapRatio > GOOD_OVERLAP_RATIO || bestInsideRatio < GOOD_INSIDE_RATIO) {
      const rings = [roomPolygon, ...regionRects.map(rectToRing)];
      const pole = Polylabel.findPoleOfInaccessibility(rings, 8);
      const poleOffset = { x: pole.center.x - this._position.x, y: pole.center.y - this._position.y };
      const { score } = this._scoreOffset(poleOffset, size, regionRects, roomPolygon);
      if (score < bestScore) {
        bestOffset = poleOffset;
      }
    }

    return bestOffset;
  }

  private _scoreOffset(
    offset: IPoint2d,
    size: { width: number; height: number },
    regionRects: IRect[],
    roomPolygon: IPoint2d[]
  ) {
    const center = { x: this._position.x + offset.x, y: this._position.y + offset.y };
    const rect: IRect = {
      left: center.x - size.width / 2,
      right: center.x + size.width / 2,
      top: center.y - size.height / 2,
      bottom: center.y + size.height / 2,
      width: size.width,
      height: size.height
    };

    // 标签与分区标签的重叠比例
    const overlapRatio = calcOverlapRatio(rect, regionRects);
    // 标签 4 个角点落在房间内的比例
    const corners: IPoint2d[] = [
      { x: rect.left, y: rect.top },
      { x: rect.right, y: rect.top },
      { x: rect.right, y: rect.bottom },
      { x: rect.left, y: rect.bottom }
    ];
    const insideCount = corners.filter((corner) => Curve2dUtils.isPointInPolygon(corner, roomPolygon)).length;
    const insideRatio = insideCount / corners.length;
    // 标签在房间外的惩罚
    const outsidePenalty = Math.max(0, 1 - insideRatio) * 10;
    // 候选偏移向量长度
    const distancePenalty = Math.hypot(offset.x, offset.y) * 0.001;
    // 综合得分，重叠比例越小越好，完全在房间内最好，偏移越小越好
    const score = overlapRatio + outsidePenalty + distancePenalty;

    return { score, overlapRatio, insideRatio };
  }

  /** 房间外轮廓的屏幕坐标多边形。洞不参与避让（锚点计算已避开洞，此处忽略是可接受的近似） */
  private _getScreenPolygon(): IPoint2d[] {
    const {
      renderer,
      scene2d: { camera }
    } = globalStores.editorStateStore.sceneInfo;
    const path = dispersePath(getPathFromEdges(this._data.pathInfos));
    return path.map((point) => worldPosToClientPos(point, camera, renderer));
  }
}

function calcOverlapRatio(roomRect: IRect, regionRects: IRect[]) {
  const roomArea = roomRect.width * roomRect.height;
  if (roomArea <= 0) {
    return 0;
  }

  const overlapArea = regionRects.reduce((sum, regionRect) => {
    return sum + calcRectIntersectArea(roomRect, regionRect);
  }, 0);

  return overlapArea / roomArea;
}

function calcRectIntersectArea(a: IRect, b: IRect) {
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.right, b.right);
  const top = Math.max(a.top, b.top);
  const bottom = Math.min(a.bottom, b.bottom);
  const width = right - left;
  const height = bottom - top;

  if (width <= 0 || height <= 0) {
    return 0;
  }

  return width * height;
}

function rectToRing(rect: IRect): IPoint2d[] {
  return [
    { x: rect.left, y: rect.top },
    { x: rect.right, y: rect.top },
    { x: rect.right, y: rect.bottom },
    { x: rect.left, y: rect.bottom }
  ];
}
